<template>
  <div 
    v-show="!loading"
    ref="bienvenidaRef"
    class="page-wrapper"
    :class="{ 'compact-view': asistenciaConfirmada }"
  >
    <div class="card-scene">
      <div ref="formCardRef" class="tarjeta-fisica">
        <div class="inner-border">
          
          <!-- PANTALLA FINAL DE AGRADECIMIENTO -->
          <div v-if="asistenciaConfirmada" class="confirmation-success text-center">
            <h2 class="thank-you-title">¡Gracias por confirmar tu asistencia!</h2>
            <p class="thank-you-subtitle"></p>
          </div>

          <!-- FORMULARIO DE PASOS -->
          <div v-else class="form-content">
            <div class="form-header">
              <h2 ref="tituloAnimado" class="form-title">
                Confirmar<br><span class="subtitulo">Asistencia</span>
              </h2>
              
              <div class="progress-wrapper">
                <div class="progress-track">
                  <div 
                    class="progress-fill" 
                    :style="{ width: ((currentStep - 1) / (totalSteps - 1)) * 100 + '%' }"
                  ></div>
                </div>
                <span class="step-counter">PASO {{ currentStep }} / {{ totalSteps }}</span>
              </div>
            </div>

            <form @submit.prevent="handleSubmit" class="confirmation-form">
              <div v-if="currentStep === 1" class="step-box">
                <div class="form-group">
                  <label class="form-label">Nombre Completo</label>
                  <input v-model.trim="name" type="text" class="nice-input" placeholder="Ej. Ana Karen..." required>
                </div>
                <div class="form-group">
                  <label class="form-label">Teléfono</label>
                  <input v-model.trim="phone" type="tel" class="nice-input" placeholder="10 dígitos" required pattern="[0-9]{10}">
                </div>
              </div>

              <div v-if="currentStep === 2 && !isXvEvent" class="step-box">
                <label class="form-label centered">¿De parte de quién nos acompañas?</label>
                <div class="radio-grid">
                  <label class="radio-card" :class="{ active: invitedBy === 'novia' }">
                    <input v-model="invitedBy" type="radio" value="novia" hidden required>
                    <span>LA NOVIA</span>
                  </label>
                  <label class="radio-card" :class="{ active: invitedBy === 'novio' }">
                    <input v-model="invitedBy" type="radio" value="novio" hidden>
                    <span>EL NOVIO</span>
                  </label>
                </div>
              </div>

              <div v-if="currentStep === asistenciaStep" class="step-box">
                <label class="form-label centered">¿Podremos contar con tu presencia?</label>
                <div class="radio-grid">
                  <label class="radio-card confirm" :class="{ active: attending === 'si' }">
                    <input v-model="attending" type="radio" value="si" hidden required>
                    <span>SÍ, ASISTIRÉ</span>
                  </label>
                  <label class="radio-card deny" :class="{ active: attending === 'no' }">
                    <input v-model="attending" type="radio" value="no" hidden>
                    <span>LO SIENTO, NO PUEDO</span>
                  </label>
                </div>
              </div>

              <div v-if="currentStep === totalSteps && attending !== 'no'" class="step-box">
                <label class="form-label centered">Confirma el número de personas</label>
                <div class="select-wrapper">
                  <select v-model.number="guests" class="nice-select" required>
                    <option :value="null" disabled>Selecciona...</option>
                    <option v-for="n in guestsOptions" :key="n" :value="n">{{ n }} {{ n === 1 ? 'Persona' : 'Personas' }}</option>
                  </select>
                </div>
              </div>

              <div class="nav-buttons">
                <button type="button" class="btn-prev" @click="prevStep" v-if="currentStep > 1">Regresar</button>
                <button v-if="currentStep < totalSteps" type="button" class="btn-next" @click="nextStep">Siguiente</button>
                <button v-if="currentStep === totalSteps" type="submit" class="btn-confirm" :disabled="isSubmitting">
                  <span v-if="!isSubmitting">CONFIRMAR</span>
                  <span v-else class="spinner-white"></span>
                </button>
              </div>

              <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, nextTick } from 'vue'; 
import axios from 'axios'; 
import baseUrl from '../api'; 
import gsap from 'gsap'; 
import { ScrollTrigger } from 'gsap/ScrollTrigger'; 
 
gsap.registerPlugin(ScrollTrigger); 
 
export default { 
  name: 'AttendanceConfirmation', 
  props: { 
    idInvitado: { type: Number, default: null }, 
    nombre: { type: String, default: '' }, 
    idFestejado: { type: Number, default: null } 
  }, 
  setup(props) { 
    const name = ref(''); 
    const phone = ref(''); 
    const invitedBy = ref(''); 
    const attending = ref(''); 
    const guests = ref(null); 
    const guestsOptions = ref([1, 2]); 
    const loading = ref(true); 
    const isSubmitting = ref(false); 
    const errorMessage = ref(''); 
    const asistenciaConfirmada = ref(false); 
    const currentStep = ref(1); 
    const formCardRef = ref(null); 
    const tituloAnimado = ref(null); 
 
    const isXvEvent = computed(() => { 
      const eventName = props.nombre?.toLowerCase(); 
      return ['xv', 'ym'].includes(eventName); 
    }); 
 
    const totalSteps = computed(() => isXvEvent.value ? 3 : 4); 
    const asistenciaStep = computed(() => isXvEvent.value ? 2 : 3); 
 
    const animateStep = () => { 
      nextTick(() => { 
        const box = formCardRef.value?.querySelector('.step-box'); 
        if (!box) return; 
 
        gsap.fromTo( 
          box, 
          { opacity: 0, x: 20 }, 
          { opacity: 1, x: 0, duration: 0.5, ease: 'power2.out' } 
        ); 
      }); 
    }; 
 
    const nextStep = async () => { 
      if (currentStep.value === 1 && (!name.value.trim() || !phone.value.trim())) { 
        errorMessage.value = 'Por favor completa tus datos.'; 
        return; 
      } 
 
      if (currentStep.value === 2 && !isXvEvent.value && !invitedBy.value) { 
        errorMessage.value = 'Selecciona una opción.'; 
        return; 
      } 
 
      if (currentStep.value === asistenciaStep.value) { 
        if (!attending.value) { 
          errorMessage.value = 'Selecciona tu asistencia.'; 
          return; 
        } 
 
        if (attending.value === 'no') { 
          currentStep.value = totalSteps.value; 
          await nextTick(); 
          handleSubmit(); 
          return; 
        } 
      } 
 
      errorMessage.value = ''; 
 
      if (currentStep.value < totalSteps.value) { 
        currentStep.value++; 
        animateStep(); 
      } 
    }; 
 
    const prevStep = () => { 
      if (currentStep.value > 1) { 
        currentStep.value--; 
        animateStep(); 
      } 
    }; 
 
    const fetchGuestLimit = async () => { 
      // Si no existe idInvitado, permitir hasta 5 personas
      if (!props.idInvitado) { 
        guestsOptions.value = [1, 2, 3, 4, 5]; 
        loading.value = false; 
        return; 
      } 
 
      try { 
        const response = await axios.get( 
          `${baseUrl}/${props.nombre ? `${props.nombre}/` : ''}${props.idFestejado ? `${props.idFestejado}/` : ''}${props.idInvitado}` 
        ); 
 
        const numPases = Number(response.data.num_pase); 
 
        guestsOptions.value = 
          numPases > 0 
            ? Array.from({ length: numPases }, (_, i) => i + 1) 
            : [1, 2, 3, 4, 5]; 
 
      } catch (e) { 
        console.error('Error fetching pases:', e); 
        guestsOptions.value = [1, 2, 3, 4, 5]; 
      } finally { 
        loading.value = false; 
      } 
    }; 
 
    const handleSubmit = async () => { 
      isSubmitting.value = true; 
      errorMessage.value = ''; 
 
      try { 
        const formData = { 
          nombre_asistencia: name.value.trim(), 
          telefono_invitado: phone.value.trim(), 
          invitedby: isXvEvent.value ? 'novia' : invitedBy.value, 
          asistira: attending.value, 
          id_festejada: props.idFestejado, 
          id_invitado: props.idInvitado, 
          num_pases: Number(attending.value === 'no' ? 0 : (guests.value ?? 0)), 
        }; 
 
        await axios.post(`${baseUrl}/asistencia`, formData); 
        asistenciaConfirmada.value = true; 
 
      } catch (e) { 
        errorMessage.value = 'Error al enviar la confirmación'; 
      } finally { 
        isSubmitting.value = false; 
      } 
    }; 
 
    onMounted(() => { 
      fetchGuestLimit(); 
      animateStep(); 
    }); 
 
    return { 
      name, 
      phone, 
      invitedBy, 
      attending, 
      guests, 
      guestsOptions, 
      loading, 
      isSubmitting, 
      errorMessage, 
      isXvEvent, 
      asistenciaConfirmada, 
      currentStep, 
      nextStep, 
      prevStep, 
      handleSubmit, 
      totalSteps, 
      asistenciaStep, 
      formCardRef, 
      tituloAnimado 
    }; 
  } 
}; 
</script>
<style scoped>
.page-wrapper {
  width: 100%;
  min-height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
 background-color: #F4F1E9; ; 
  padding: 60px 20px;
  perspective: 1200px;
}

.card-scene {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 420px;
}

.tarjeta-fisica {
  background: #ffffff;
  width: 100%;
  padding: 10px;
  box-shadow: 0 15px 45px rgba(0,0,0,0.06);
  border-radius: 2px;
  background-image: url('https://www.transparenttextures.com/patterns/paper-fibers.png');
}

.inner-border {
  border: 1px solid #e8e4de;
  padding: 40px 25px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 380px;
}

/* MENSAJE DE CONFIRMACIÓN FINAL */
.confirmation-success {
  padding: 20px 10px;
  text-align: center;
}

.thank-you-title {
  font-family: 'Playfair Display', serif;
  font-size: 26px;
  color: #7a5a35;
  line-height: 1.3;
  margin-bottom: 15px;
  font-weight: 600;
}

.thank-you-subtitle {
  font-family: 'Playfair Display', serif;
  font-size: 16px;
  color: #555;
  line-height: 1.5;
  font-style: italic;
}

.form-content {
  width: 100%;
}

.form-title {
  font-family: 'Playfair Display', serif;
  font-size: 28px;
  text-align: center;
  color: #C6A66A;
  line-height: 1.1;
  margin-bottom: 30px;
}

.subtitulo {
  font-size: 18px;
  color: #686D55;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.progress-wrapper {
  margin-bottom: 30px;
  text-align: center;
}

.progress-track {
  height: 2px;
  background: #e8e4de;
  width: 60%;
  margin: 0 auto 10px;
}

.progress-fill {
  height: 100%;
  background: #7a5a35;
  transition: width 0.4s ease;
}

.step-counter {
  font-family: 'Karla', sans-serif;
  font-size: 10px;
  letter-spacing: 2px;
  color: #7e7d7d;
}

.form-group { margin-bottom: 20px; }

.form-label {
  font-family: 'Karla', sans-serif;
  font-size: 11px;
  color: #5a5858;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 10px;
  display: block;
}

.form-label.centered { text-align: center; margin-bottom: 25px; }

.nice-input {
  width: 100%;
  border: none;
  border-bottom: 1px solid #e8e4de;
  padding: 12px 0;
  font-family: 'Playfair Display', serif;
  font-size: 17px;
  color: #333;
  transition: border-color 0.3s;
  background: transparent;
}

.nice-input:focus {
  outline: none;
  border-color: #7a5a35;
}

.radio-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.radio-card {
  border: 1px solid #e8e4de;
  padding: 18px 10px;
  text-align: center;
  cursor: pointer;
  font-family: 'Karla', sans-serif;
  font-size: 11px;
  letter-spacing: 1px;
  color: #383636;
  transition: all 0.3s;
}

.radio-card.active {
  background: #fdfaf7;
  border-color: #7a5a35;
  color: #7a5a35;
}

.nice-select {
  width: 100%;
  border: 1px solid #e8e4de;
  padding: 14px;
  font-family: 'Playfair Display', serif;
  background: transparent;
  border-radius: 0;
  appearance: none;
}

.nav-buttons {
  margin-top: auto;
  display: flex;
  justify-content: center;
  gap: 12px;
  padding-top: 35px;
}

.btn-next, .btn-confirm, .btn-prev {
  min-width: 130px;
  padding: 14px 28px;
  border: none;
  border-radius: 50px;
  background: #686D55;
  color: #F4F1E9;
  font-family: 'Karla', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all .3s ease;
  box-shadow: 0 10px 25px rgba(111, 82, 56, .25);
}

.btn-prev {
  background: #fff;
  color: #7a5a35;
  border: 1px solid #d9d2ca;
  box-shadow: none;
}

.btn-next:hover:not(:disabled), .btn-confirm:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 16px 35px rgba(111, 82, 56, .35);
  background: linear-gradient(135deg, #9b7757, #7d5d40);
}

.spinner-white {
  width: 15px;
  height: 15px;
  border: 2px solid #ffffff;
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-text {
  color: #c0392b;
  font-size: 11px;
  text-align: center;
  margin-top: 15px;
}
</style>